import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import config from "./config";
import { loadSubscriptionPlaces } from "./places";

const monthOptions = Array.from({ length: 12 }, (_, index) => index + 1);
const paymentOptions = [1, 2, 3, 4];
const todayIso = () => new Date().toISOString().slice(0, 10);
const currency = "\u20aa";
const normalizeCouponCode = (value) =>
  String(value || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7);
const parseResponse = async (res) => {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (_) {
    return { error: text };
  }
};

export default function SubscriptionForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCouponCode = normalizeCouponCode(
    searchParams.get("coupon-code") || searchParams.get("couponCode") || searchParams.get("coupon") || ""
  );
  const autoCouponKeyRef = useRef("");
  const [places, setPlaces] = useState([]);
  const [carId, setCarId] = useState("");
  const [months, setMonths] = useState(1);
  const [payments, setPayments] = useState(1);
  const [startDate, setStartDate] = useState(todayIso());
  const [couponCode, setCouponCode] = useState(initialCouponCode);
  const [couponInfo, setCouponInfo] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const placeId = searchParams.get("place-id") || "";

  useEffect(() => {
    let ignore = false;
    loadSubscriptionPlaces(config.apiUrl)
      .then((list) => {
        if (ignore) return;
        setPlaces(list);
      });
    return () => {
      ignore = true;
    };
  }, []);

  const selectedPlace = useMemo(
    () => places.find((place) => place.id === placeId) || null,
    [places, placeId]
  );
  const monthlyPrice = Number(selectedPlace?.monthlyPrice || 0);
  const subtotalPrice = Number((monthlyPrice * Number(months || 1)).toFixed(2));
  const normalizedCouponCode = normalizeCouponCode(couponCode);
  const activeCoupon =
    couponInfo && couponInfo.code === normalizedCouponCode && couponInfo.placeId === selectedPlace?.id
      ? couponInfo
      : null;
  const discountAmount = activeCoupon ? Math.min(Number(activeCoupon.discountAmount || 0), subtotalPrice) : 0;
  const totalPrice = Number((subtotalPrice - discountAmount).toFixed(2));
  const installmentsAllowed = totalPrice >= 800;
  const cleanCarId = carId.trim().replace(/\s+/g, "").toUpperCase();

  useEffect(() => {
    setCouponInfo(null);
  }, [placeId, months]);

  useEffect(() => {
    if (!installmentsAllowed && payments !== 1) setPayments(1);
  }, [installmentsAllowed, payments]);

  const checkCoupon = async () => {
    setError("");
    setCouponInfo(null);
    if (!selectedPlace) {
      setError("\u05d9\u05e9 \u05dc\u05d1\u05d7\u05d5\u05e8 \u05d7\u05e0\u05d9\u05d5\u05df");
      return;
    }
    if (normalizedCouponCode.length !== 7) {
      setError("\u05d9\u05e9 \u05dc\u05d4\u05d6\u05d9\u05df \u05e7\u05d5\u05d3 \u05e7\u05d5\u05e4\u05d5\u05df \u05d1\u05df 7 \u05ea\u05d5\u05d5\u05d9\u05dd");
      return;
    }

    setCouponLoading(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/public-subscriptions/coupon-check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          placeId: selectedPlace.id,
          months: Number(months),
          couponCode: normalizedCouponCode,
        }),
      });
      const json = await parseResponse(res);
      if (!res.ok || !json?.coupon) {
        throw new Error(json?.error || "\u05e7\u05d5\u05d3 \u05e7\u05d5\u05e4\u05d5\u05df \u05dc\u05d0 \u05ea\u05e7\u05d9\u05df");
      }
      setCouponCode(json.coupon.code);
      setCouponInfo({
        placeId: selectedPlace.id,
        code: json.coupon.code,
        discountAmount: Number(json.coupon.discountAmount || 0),
      });
    } catch (err) {
      setError(err.message || "\u05dc\u05d0 \u05e0\u05d9\u05ea\u05df \u05dc\u05d1\u05d3\u05d5\u05e7 \u05e7\u05d5\u05e4\u05d5\u05df");
    } finally {
      setCouponLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedPlace || !initialCouponCode) return;
    const key = `${selectedPlace.id}|${months}|${initialCouponCode}`;
    if (normalizedCouponCode !== initialCouponCode) {
      setCouponCode(initialCouponCode);
      return;
    }
    if (autoCouponKeyRef.current === key) return;
    autoCouponKeyRef.current = key;
    checkCoupon();
    // checkCoupon intentionally uses the current form state for the selected place/months.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlace, months, initialCouponCode, normalizedCouponCode]);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!cleanCarId) {
      setError("יש להזין מספר רכב");
      return;
    }
    if (!selectedPlace) {
      setError("יש לבחור חניון");
      return;
    }
    if (!startDate || startDate < todayIso()) {
      setError("יש לבחור תאריך התחלה מהיום והלאה");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/public-subscriptions/payment-page`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          carId: cleanCarId,
          noPhoneNumber: true,
          placeId: selectedPlace.id,
          months: Number(months),
          payments: installmentsAllowed ? Number(payments) : 1,
          startDate,
          couponCode: normalizedCouponCode || undefined,
          returnBaseUrl: window.location.origin,
          syncToDevice: true,
        }),
      });
      const json = await parseResponse(res);
      if (!res.ok || !json?.paymentLink) {
        throw new Error(json?.error || "לא ניתן לפתוח תשלום");
      }
      console.log("[subscriptions] payment redirect urls", {
        appBaseUrl: json.appBaseUrl,
        successUrl: json.successUrl,
        failureUrl: json.failureUrl,
      });
      const summary = {
        ...json.summary,
        noPhoneNumber: true,
      };
      sessionStorage.setItem("subscriptionSession", json.session);
      sessionStorage.setItem("subscriptionSummary", JSON.stringify(summary));
      sessionStorage.setItem("subscriptionPaymentLink", json.paymentLink);
      navigate("/payment", { state: { paymentLink: json.paymentLink, summary } });
    } catch (err) {
      setError(err.message || "תקלה בפתיחת התשלום");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="page" dir="rtl">
      <section className="panel">
        <img src="/logo.png" alt="Blue Parking" className="logo" />
        <h1>מנוי חניה</h1>

        {!selectedPlace ? (
          <div className="form">
            <p className="error">לא נבחר חניון תקין</p>
            <Link to="/">בחירת חניון</Link>
          </div>
        ) : (
        <form onSubmit={submit} className="form">
          <div className="selected-place">
            <span>חניון</span>
            <strong>{selectedPlace.name}</strong>
            {/*<Link className="text-link" to="/">החלפה</Link>*/}
          </div>

          <label>
            מספר רכב
            <input
              value={carId}
              onChange={(event) => setCarId(event.target.value)}
              placeholder="לדוגמה 1234567"
              autoComplete="off"
              required
              dir="ltr"
            />
          </label>
          <label>
            תקופת מנוי
            <select required value={months} onChange={(event) => setMonths(Number(event.target.value))}>
              {monthOptions.map((value) => (
                <option key={value} value={value}>
                  {value} {value === 1 ? "חודש" : "חודשים"}
                </option>
              ))}
            </select>
          </label>

          <label>
            תאריך התחלה
            <input
              type="date"
              value={startDate}
              min={todayIso()}
              onChange={(event) => setStartDate(event.target.value)}
              required
            />
          </label>

          <label>
            {"קוד קופון"}
            <div className="coupon-row">
              <input
                value={couponCode}
                onChange={(event) => {
                  setCouponCode(normalizeCouponCode(event.target.value));
                  setCouponInfo(null);
                  setError("");
                }}
                placeholder="ABC1234"
                autoComplete="off"
                dir="ltr"
                maxLength={7}
              />
              <button
                type="button"
                className="secondary coupon-button"
                onClick={checkCoupon}
                disabled={couponLoading || !normalizedCouponCode}
              >
                {couponLoading ? "..." : "הפעל"}
              </button>
            </div>
          </label>

          {installmentsAllowed ? (
            <label>
              מספר תשלומים
              <select required value={payments} onChange={(event) => setPayments(Number(event.target.value))}>
                {paymentOptions.map((value) => (
                  <option key={value} value={value}>
                    {value === 1 ? "תשלום אחד" : `${value} תשלומים`}
                  </option>
                ))}
              </select>
              <small className="field-hint">בסכום של 800 ₪ ומעלה ניתן לפרוס עד 4 תשלומים</small>
            </label>
          ) : null}

          <div className="summary">
            <div>
              <span>תחילת מנוי</span>
              <strong>{startDate ? new Date(`${startDate}T00:00:00`).toLocaleDateString("he-IL") : ""}</strong>
            </div>
            <div>
              <span>מחיר לחודש</span>
              <strong>{monthlyPrice.toLocaleString("he-IL")} ₪</strong>
            </div>
            <div>
              <span>סה"כ לתשלום</span>
              <strong>{subtotalPrice.toLocaleString("he-IL")} {currency}</strong>
            </div>
            {activeCoupon ? (
              <div>
                <span>הנחת קופון</span>
                <strong>-{discountAmount.toLocaleString("he-IL")} {currency}</strong>
              </div>
            ) : null}
            <div>
              <span>סכום לתשלום</span>
              <strong>{totalPrice.toLocaleString("he-IL")} {currency}</strong>
            </div>
            {installmentsAllowed ? (
              <div>
                <span>מספר תשלומים</span>
                <strong>{payments}</strong>
              </div>
            ) : null}
          </div>

          {error ? <p className="error">{error}</p> : null}

          <button type="submit" disabled={loading || !places.length}>
            {loading ? "פותח תשלום..." : "המשך לתשלום"}
          </button>
        </form>
        )}
      </section>
    </main>
  );
}
